import { useQuery, QueryKey } from '@tanstack/react-query';
import { useHasFeatureFlag } from 'src/featureFlags/useHasFeatureFlag';
import { parseResponseDateAndTime } from 'src/common/utils/parseResponseDate';
import { SessionDTO, SimData } from 'src/common/components/Sim/constants/sim';
import { fetchSessionsDetails } from '../SimInventoryApi/simInventoryApi';
import { parseLocalization } from 'src/common/components/Sim/Inventory/utils/simListResponseParsers';

type SessionsDetailsMap = Record<string, SessionDTO>;

export type EnrichedSessionDetailsSimData = SimData & {
  lastRoamingTadig?: SessionDTO['tadig'];
  lastRoamingMcc?: SessionDTO['mcc'];
  lastRoamingMnc?: SessionDTO['mnc'];
  lastRoamingPlmn?: SessionDTO['plmn'];
  lastRoamingIsoCode?: SessionDTO['isoCode'];
  lastRoamingCountryName?: SessionDTO['countryName'];
  lastRoamingOperatorName?: SessionDTO['operatorName'];
  lastRoamingRatDisplayInfo?: SessionDTO['ratDisplayInfo'];
  lastRoamingRatType?: SessionDTO['ratType'];
};

type UseSessionsDetailsProps = {
  imsis: string[];
  queryKey: QueryKey;
};

export const SESSION_DETAILS_QUERY_KEY = 'SessionDetails';

export const enrichWithSessionDetailsMapper = (
  originalData: SimData,
  sessionsDetails?: SessionsDetailsMap,
): EnrichedSessionDetailsSimData => {
  const imsi = originalData.imsi;
  const session = imsi ? sessionsDetails?.[imsi] : undefined;
  return {
    ...originalData,
    ...(session?.sessionStatusLastUpdated !== undefined && {
      sessionStatusLastUpdated: parseResponseDateAndTime(session?.sessionStatusLastUpdated),
    }),
    cumulativeTotalUsage: (
      (session?.cumulativeInputUsage || 0) + (session?.cumulativeOutputUsage || 0)
    ).toFixed(2),
    apn: session?.apn ?? undefined,
    ...(session?.localization !== undefined && {
      localization: parseLocalization(session?.localization) || '',
    }),
    lastRoamingTadig: session?.tadig,
    lastRoamingMcc: session?.mcc,
    lastRoamingMnc: session?.mnc,
    lastRoamingPlmn: session?.plmn,
    lastRoamingIsoCode: session?.isoCode,
    lastRoamingCountryName: session?.countryName,
    lastRoamingOperatorName: session?.operatorName,
    lastRoamingRatDisplayInfo: session?.ratDisplayInfo,
    lastRoamingRatType: session?.ratType,
    mcc: session?.mcc,
    mnc: session?.mnc,
    plmn: session?.plmn,
    isoCode: session?.isoCode,
    countryName: session?.countryName,
    operatorName: session?.operatorName,
    ratDisplayInfo: session?.ratDisplayInfo,
    ratType: session?.ratType,
    rasClient: session?.rasClient ?? undefined,
    framedProtocol: session?.framedProtocol ?? undefined,
    nasIpAddress: session?.nasIpAddress ?? undefined,
    serviceType: session?.serviceType ?? undefined,
    userName: session?.userName ?? undefined,
    currentIpAddress: session?.currentIpAddress ?? undefined,
  };
};

export const useSessionsDetails = ({ imsis, queryKey }: UseSessionsDetailsProps) => {
  const isRadiusInterimsFilteringEnabled = useHasFeatureFlag('RadiusInterimsFiltering');

  return useQuery({
    queryKey,
    queryFn: async () => {
      const data = await fetchSessionsDetails(imsis);
      return data;
    },
    select: (data) => {
      const details = data.reduce<SessionsDetailsMap>((acc, session) => {
        acc[session.imsi] = { ...session };
        return acc;
      }, {});

      return details;
    },
    enabled: !!imsis?.length && isRadiusInterimsFilteringEnabled,
  });
};
